import { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Box,
  Chip,
  IconButton,
  TablePagination,
  CircularProgress,
  Alert,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import LoginIcon from "@mui/icons-material/Login";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import adminAPI, { AdminUser, AdminSubscription } from "../admin.api";
import { useNavigate } from "react-router-dom";

function UsersTab() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [userSubscriptions, setUserSubscriptions] = useState<Record<string, AdminSubscription[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loginUrlDialogOpen, setLoginUrlDialogOpen] = useState(false);
  const [generatedLoginUrl, setGeneratedLoginUrl] = useState("");
  const [selectedUserEmail, setSelectedUserEmail] = useState("");
  const [generatingUrl, setGeneratingUrl] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    email: "",
    firstName: "",
    lastName: "",
    password: "",
    isAdmin: false
  });
  const [addingUser, setAddingUser] = useState(false);
  const [sortBy, setSortBy] = useState<"createdAt" | "aiImage">("createdAt");

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminAPI.getUsers({
        page: page + 1,
        limit: rowsPerPage,
        search: search || undefined,
        sortBy: sortBy,
      });
      setUsers(response.users);
      setTotalUsers(response.pagination.total);
      
      // Fetch subscriptions for all users
      const subscriptionsPromises = response.users.map((user: AdminUser) => 
        adminAPI.getUserSubscriptions(user._id)
          .then(result => {
            // Handle different response formats
            let subscriptions: AdminSubscription[] = [];
            if (Array.isArray(result)) {
              subscriptions = result;
            } else if (result && typeof result === 'object') {
              if (Array.isArray(result.subscriptions)) {
                subscriptions = result.subscriptions;
              } else if (Array.isArray(result.data)) {
                subscriptions = result.data;
              } else if (result.subscriptions) {
                subscriptions = [result.subscriptions];
              }
            }
            return { 
              userId: user._id, 
              subscriptions
            };
          })
          .catch((err) => {
            console.error(`Failed to fetch subscriptions for user ${user._id}:`, err);
            return { userId: user._id, subscriptions: [] };
          })
      );
      
      const subscriptionsResults = await Promise.all(subscriptionsPromises);
      const subscriptionsMap: Record<string, AdminSubscription[]> = {};
      subscriptionsResults.forEach(result => {
        subscriptionsMap[result.userId] = result.subscriptions;
      });
      setUserSubscriptions(subscriptionsMap);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } } };
      setError(error.response?.data?.error?.message || "Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, rowsPerPage, sortBy]);

  const handleSearch = () => {
    setPage(0);
    fetchUsers();
  };

  const handleManageUser = (user: AdminUser) => {
    navigate(`/admin/users/${user._id}`);
  };

  const handleGenerateLoginUrl = async (user: AdminUser) => {
    setGeneratingUrl(user._id);
    try {
      const response = await adminAPI.generateLoginUrl(user._id);
      // Replace localhost:3000 with current domain if needed
      let loginUrl = response.loginUrl;
      if (loginUrl.includes('localhost:3000') && window.location.hostname !== 'localhost') {
        const currentOrigin = window.location.origin;
        loginUrl = loginUrl.replace('http://localhost:3000', currentOrigin);
      }
      setGeneratedLoginUrl(loginUrl);
      setSelectedUserEmail(response.userEmail);
      setLoginUrlDialogOpen(true);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } }; message?: string };
      setSnackbarMessage(`Failed to generate login URL: ${error.response?.data?.error?.message || error.message}`);
      setSnackbarOpen(true);
    } finally {
      setGeneratingUrl(null);
    }
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(generatedLoginUrl);
      setSnackbarMessage("Login URL copied to clipboard!");
      setSnackbarOpen(true);
      // Close the dialog after successful copy
      handleCloseDialog();
    } catch (err) {
      setSnackbarMessage("Failed to copy URL");
      setSnackbarOpen(true);
    }
  };

  const handleCloseDialog = () => {
    setLoginUrlDialogOpen(false);
    setGeneratedLoginUrl("");
    setSelectedUserEmail("");
  };

  const handleAddUser = async () => {
    if (!newUserForm.email || !newUserForm.password) {
      setSnackbarMessage("Email and password are required");
      setSnackbarOpen(true);
      return;
    }

    setAddingUser(true);
    try {
      await adminAPI.createUser(newUserForm);
      setSnackbarMessage("User created successfully");
      setSnackbarOpen(true);
      setAddUserDialogOpen(false);
      setNewUserForm({
        email: "",
        firstName: "",
        lastName: "",
        password: "",
        isAdmin: false
      });
      fetchUsers(); // Refresh the user list
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } }; message?: string };
      setSnackbarMessage(`Failed to create user: ${error.response?.data?.error?.message || error.message}`);
      setSnackbarOpen(true);
    } finally {
      setAddingUser(false);
    }
  };

  const handleCloseAddUserDialog = () => {
    setAddUserDialogOpen(false);
    setNewUserForm({
      email: "",
      firstName: "",
      lastName: "",
      password: "",
      isAdmin: false
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "success";
      case "trialing":
        return "info";
      case "cancelled":
        return "warning";
      case "payment-failed":
      case "banned":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Box>
      {/* Search Bar and Add User */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", gap: 2, justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <TextField
              placeholder="Search by email, name, or user ID"
              variant="outlined"
              size="medium"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
              sx={{
                width: 400,
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.paper',
                }
              }}
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              size="large"
              sx={{
                px: 3,
                textTransform: 'none',
              }}
            >
              Search
            </Button>
            <FormControl size="medium" sx={{ minWidth: 180 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={(e) => setSortBy(e.target.value as "createdAt" | "aiImage")}
                sx={{
                  bgcolor: 'background.paper',
                }}
              >
                <MenuItem value="createdAt">Join Date</MenuItem>
                <MenuItem value="aiImage">AI Image Usage</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Button
            variant="contained"
            color="success"
            startIcon={<PersonAddIcon />}
            onClick={() => setAddUserDialogOpen(true)}
            size="large"
            sx={{
              px: 3,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Add User
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer 
        component={Paper} 
        elevation={0}
        sx={{ 
          borderRadius: 3,
          overflow: 'hidden',
          '& .MuiTable-root': {
            borderCollapse: 'separate',
            borderSpacing: 0,
          }
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow sx={{ 
                  bgcolor: 'background.paper',
                  '& .MuiTableCell-head': {
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    borderBottom: '2px solid',
                    borderColor: 'divider',
                    py: 2,
                  }
                }}>
                  <TableCell>User</TableCell>
                  <TableCell>Processor</TableCell>
                  <TableCell align="center">Subscriptions</TableCell>
                  <TableCell align="center">AI Image Usage</TableCell>
                  <TableCell>Joined</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                      <Typography color="text.secondary" variant="body2">
                        No users found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user, index) => (
                    <TableRow 
                      key={user._id}
                      sx={{
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                        '& .MuiTableCell-body': {
                          borderBottom: index === users.length - 1 ? 'none' : '1px solid',
                          borderColor: 'divider',
                          py: 2,
                          fontSize: '0.875rem',
                        },
                        transition: 'background-color 0.2s',
                      }}
                    >
                      <TableCell>
                        <Tooltip title={`User ID: ${user._id}`} placement="top" arrow>
                          <Box>
                            <Typography 
                              variant="body2" 
                              fontWeight={600}
                              sx={{ 
                                color: 'primary.main',
                                cursor: 'pointer',
                                textDecoration: 'none',
                                '&:hover': {
                                  textDecoration: 'underline'
                                }
                              }}
                              onClick={() => handleManageUser(user)}
                            >
                              {user.email}
                              {user.isAdmin && (
                                <Chip 
                                  label="Admin" 
                                  color="primary" 
                                  size="small"
                                  sx={{ 
                                    ml: 1,
                                    fontWeight: 600,
                                    borderRadius: 1,
                                    height: 18,
                                    fontSize: '0.65rem'
                                  }}
                                />
                              )}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {user.firstName} {user.lastName}
                            </Typography>
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {user.processor ? (
                          <Chip
                            label={user.processor}
                            variant="outlined"
                            size="small"
                            sx={{ 
                              borderRadius: 1.5,
                              textTransform: 'uppercase',
                              fontSize: '0.7rem',
                              fontWeight: 600
                            }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {(() => {
                          const subs = Array.isArray(userSubscriptions[user._id]) ? userSubscriptions[user._id] : [];
                          const activeSubs = subs.filter(s => s.status === "active" || s.status === "trial");
                          const totalCount = subs.length;
                          
                          if (totalCount === 0) {
                            return (
                              <Typography variant="body2" color="text.secondary">
                                -
                              </Typography>
                            );
                          }
                          
                          return (
                            <Tooltip
                              title={
                                <Box sx={{ p: 0.5 }}>
                                  <Typography variant="caption" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
                                    Subscriptions ({totalCount} total)
                                  </Typography>
                                  {subs.map((sub, idx) => (
                                    <Box key={sub._id} sx={{ mb: idx === subs.length - 1 ? 0 : 1 }}>
                                      <Typography variant="caption" component="div">
                                        • {sub.status.charAt(0).toUpperCase() + sub.status.slice(1)}
                                        {sub.planId?.name && ` - ${sub.planId.name}`}
                                      </Typography>
                                      <Typography variant="caption" component="div" color="text.secondary" sx={{ pl: 1.5 }}>
                                        {sub.processor} • Started {new Date(sub.startDate).toLocaleDateString()}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              }
                              placement="left"
                              arrow
                            >
                              <Chip
                                label={`${activeSubs.length}/${totalCount}`}
                                size="small"
                                color={activeSubs.length > 0 ? "success" : "default"}
                                variant={activeSubs.length > 0 ? "filled" : "outlined"}
                                sx={{
                                  cursor: 'pointer',
                                  fontWeight: 600,
                                  borderRadius: 1.5,
                                }}
                              />
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                      <TableCell align="center">
                        {user.aiImage !== undefined && user.aiImageLimit !== undefined ? (
                          (() => {
                            const isUnlimited = user.aiImageLimit >= 999999 || user.aiImageLimit === -1;
                            return (
                              <Tooltip
                                title={
                                  <Box sx={{ p: 0.5 }}>
                                    <Typography variant="caption" fontWeight={600}>
                                      AI Image Usage
                                    </Typography>
                                    <Typography variant="caption" component="div">
                                      Used: {user.aiImage || 0}
                                    </Typography>
                                    <Typography variant="caption" component="div">
                                      Limit: {isUnlimited ? 'Unlimited' : user.aiImageLimit || 0}
                                    </Typography>
                                    {!isUnlimited && (
                                      <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                                        Remaining: {(user.aiImageLimit || 0) - (user.aiImage || 0)}
                                      </Typography>
                                    )}
                                  </Box>
                                }
                                placement="left"
                                arrow
                              >
                                <Box>
                                  <Typography variant="body2" fontWeight={600}>
                                    {user.aiImage || 0} / {isUnlimited ? '∞' : user.aiImageLimit || 0}
                                  </Typography>
                                  {!isUnlimited && (
                                    <Typography variant="caption" color="text.secondary">
                                      {user.aiImageLimit > 0
                                        ? `${Math.round(((user.aiImage || 0) / user.aiImageLimit) * 100)}%`
                                        : '0%'}
                                    </Typography>
                                  )}
                                  {isUnlimited && (
                                    <Typography variant="caption" color="success.main">
                                      Unlimited
                                    </Typography>
                                  )}
                                </Box>
                              </Tooltip>
                            );
                          })()
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {new Date(user.createdAt).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(user.createdAt).toLocaleTimeString()}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 20, 50]}
              component="div"
              count={totalUsers}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
                '& .MuiTablePagination-toolbar': {
                  pl: 2,
                  pr: 2,
                }
              }}
            />
          </>
        )}
      </TableContainer>

      {/* Login URL Dialog */}
      <Dialog
        open={loginUrlDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon color="success" />
            <Typography variant="h6">Login URL Generated</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Important:</strong> Open this link in an incognito/private browser tab to avoid being logged out of your admin session.
          </Alert>
          <Alert severity="info" sx={{ mb: 2 }}>
            This one-time login link will expire in <strong>1 hour</strong> and can only be used once.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            User: <strong>{selectedUserEmail}</strong>
          </Typography>
          <TextField
            fullWidth
            value={generatedLoginUrl}
            InputProps={{
              readOnly: true,
              sx: { fontFamily: 'monospace', fontSize: '0.875rem' },
              endAdornment: (
                <Tooltip title="Copy to clipboard">
                  <IconButton onClick={handleCopyUrl} edge="end" aria-label="Copy login URL to clipboard">
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'grey.50',
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog
        open={addUserDialogOpen}
        onClose={handleCloseAddUserDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonAddIcon color="success" />
            <Typography variant="h6">Add New User</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Email Address *"
              type="email"
              value={newUserForm.email}
              onChange={(e) => setNewUserForm(prev => ({ ...prev, email: e.target.value }))}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="First Name"
                value={newUserForm.firstName}
                onChange={(e) => setNewUserForm(prev => ({ ...prev, firstName: e.target.value }))}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
              <TextField
                fullWidth
                label="Last Name"
                value={newUserForm.lastName}
                onChange={(e) => setNewUserForm(prev => ({ ...prev, lastName: e.target.value }))}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Box>
            <TextField
              fullWidth
              label="Password *"
              type="password"
              value={newUserForm.password}
              onChange={(e) => setNewUserForm(prev => ({ ...prev, password: e.target.value }))}
              required
              helperText="Minimum 5 characters"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <input
                type="checkbox"
                id="isAdmin"
                checked={newUserForm.isAdmin}
                onChange={(e) => setNewUserForm(prev => ({ ...prev, isAdmin: e.target.checked }))}
              />
              <label htmlFor="isAdmin">
                <Typography variant="body2">
                  Grant admin privileges
                </Typography>
              </label>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseAddUserDialog}
            disabled={addingUser}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleAddUser}
            disabled={addingUser || !newUserForm.email || !newUserForm.password}
            startIcon={addingUser ? <CircularProgress size={20} /> : <PersonAddIcon />}
          >
            {addingUser ? "Creating..." : "Create User"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

export default UsersTab;